"use client";

import React from "react";
import { SlidersHorizontal, ShieldCheck, ArrowRight, Boxes, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
interface SpecItem {
  label: L6;
  value: string;
}
const specifications: SpecItem[] = [{
  label: {
    en: "Standard alloys",
    zh: "标准合金",
    es: "Aleaciones estándar",
    de: "Standardlegierungen",
    fr: "Alliages standard",
    pt: "Ligas padrão"
  },
  value: "AISI 304 / 304L / 316 / 316L"
}, {
  label: {
    en: "Filtration range",
    zh: "过滤精度范围",
    es: "Rango de filtración",
    de: "Filterbereich",
    fr: "Plage de filtration",
    pt: "Faixa de filtração"
  },
  value: "20 µm to 850 µm"
}, {
  label: {
    en: "Screen mesh range",
    zh: "筛网目数范围",
    es: "Rango de mallas",
    de: "Maschenbereich",
    fr: "Plage de maillage",
    pt: "Faixa de malhas"
  },
  value: "20 to 635 Mesh"
}, {
  label: {
    en: "Pack configurations",
    zh: "组件结构形式",
    es: "Configuraciones de paquetes",
    de: "Paketkonfigurationen",
    fr: "Configurations de packs",
    pt: "Configurações de pacotes"
  },
  value: "Discs, rings, packs, ribbons"
}];
const copy = {
  eyebrow: {
    en: "Polymer & Extrusion Filtration",
    zh: "聚合物与挤出过滤",
    es: "Filtración de polímeros y extrusión",
    de: "Polymer- & Extrusionsfiltration",
    fr: "Filtration de polymères et d'extrusion",
    pt: "Filtração de polímeros e extrusão"
  },
  standardTag: {
    en: "ISO 9044 / ASTM E2016",
    zh: "ISO 9044 / ASTM E2016",
    es: "ISO 9044 / ASTM E2016",
    de: "ISO 9044 / ASTM E2016",
    fr: "ISO 9044 / ASTM E2016",
    pt: "ISO 9044 / ASTM E2016"
  },
  h1: {
    en: "Wire mesh screen packs for continuous polymer and plastic extrusion.",
    zh: "专为连续聚合物与塑料挤出工艺定制的精密金属滤网组。",
    es: "Paquetes de mallas de alambre para extrusión continua de polímeros y plásticos.",
    de: "Drahtgewebe-Siebpakete für die kontinuierliche Polymer- und Kunststoffextrusion.",
    fr: "Packs de toiles métalliques pour l'extrusion continue de polymères et plastiques.",
    pt: "Pacotes de malhas de arame para extrusão contínua de polímeros e plásticos."
  },
  lead: {
    en: "Maintain steady melt flow and filter particulate contaminants with precision-woven stainless steel screen packs, sintered discs, and multi-layer assemblies built for continuous extrusion equipment.",
    zh: "采用高精度编织不锈钢滤网组、烧结滤盘及多层结构组件，在挤出工况下稳定熔体流速并高效截留微粒杂质。",
    es: "Mantenga un flujo continuo de fundido y retenga contaminantes con paquetes de mallas de acero inoxidable, discos sinterizados y conjuntos multicapa para equipos de extrusión.",
    de: "Gewährleisten Sie gleichmäßigen Schmelzefluss und filtrieren Sie Verunreinigungen mit präzisionsgewebten Edelstahlsiebpaketen, Sinterdisks und Mehrlagenbaugruppen für kontinuierliche Extrusionsanlagen.",
    fr: "Maintenez un écoulement stable de matière fondue et filtrez les impuretés grâce à des packs de toiles inox, des disques frittés et des ensembles multicouches pour extrudeuses.",
    pt: "Mantenha um fluxo contínuo de fundido e retenha contaminantes com pacotes de telas de aço inoxidável, discos sinterizados e conjuntos multicamadas para linhas contínuas de extrusão."
  },
  relatedCta: {
    en: "Polymer filtration overview",
    zh: "聚合物过滤概览",
    es: "Descripción de filtración de polímeros",
    de: "Übersicht Polymerfiltration",
    fr: "Aperçu filtration des polymères",
    pt: "Visão geral de filtração de polímeros"
  },
  imgAlt: {
    en: "Multi-layer stainless steel extruder screen pack assembly",
    zh: "多层不锈钢挤出机滤网组装配件",
    es: "Conjunto de paquete de mallas multicapa de acero inoxidable para extrusora",
    de: "Mehrlagige Edelstahl-Extrudersiebpaket-Baugruppe",
    fr: "Assemblage de pack de toiles multicouches en acier inoxydable pour extrudeuse",
    pt: "Conjunto de pacote de telas multicamadas de aço inoxidável para extrusora"
  },
  overlayBadge: {
    en: "Extruder Screen Pack",
    zh: "挤出机滤网组",
    es: "Paquete de mallas para extrusora",
    de: "Extrudersiebpaket",
    fr: "Pack de toiles pour extrudeuse",
    pt: "Pacote de telas para extrusora"
  },
  cardFooterNote: {
    en: "Spot-welded, framed, or loose multi-layer discs",
    zh: "点焊、金属包边或散片多层组合滤片",
    es: "Discos multicapa soldados por puntos, con marco o sueltos",
    de: "Punktgeschweißt, gerahmt oder lose Mehrlagenronden",
    fr: "Disques multicouches soudés par points, bordés ou libres",
    pt: "Discos multicamadas ponteados, emoldurados ou avulsos"
  },
  cardFooterLink: {
    en: "OEM Custom",
    zh: "OEM定制",
    es: "OEM personalizado",
    de: "OEM-Kundenspezifisch",
    fr: "OEM sur mesure",
    pt: "OEM personalizado"
  }
};
export default function ExtrusionHero() {
  const {
    lang
  } = useLanguage();
  return <section id="extrusionfiltrationhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column: Badges, Headline, Lead & CTAs (lg:col-span-7) */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <Reveal>
              {/* Badges row: exactly two outline badges */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.eyebrow)}</span>
                </Badge>
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-2.5 py-1 font-mono mf-caption text-foreground">
                  <ShieldCheck className="size-3.5 text-primary inline" aria-hidden="true" />
                  <span>{pick(lang, copy.standardTag)}</span>
                </Badge>
              </div>

              {/* Page h1 */}
              <h1 className="font-default text-balance mf-h1 text-foreground">
                {pick(lang, copy.h1)}
              </h1>

              {/* Lead paragraph (2-3 sentences) */}
              <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
                {pick(lang, copy.lead)}
              </p>

              {/* CTAs */}
              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>

                <Button variant="outline" size="lg" asChild className="px-6 text-sm font-semibold transition-colors w-full sm:w-auto">
                  <Link to="/PolymerFiltrationPage" className="inline-flex items-center justify-center gap-2">
                    <span>{pick(lang, copy.relatedCta)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: ONE bordered Card (lg:col-span-5) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={60}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground gap-0 py-0">
                <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                  <img src="https://mutualfilter.com/wp-content/uploads/2026/09/filter-packs1.jpg" alt={pick(lang, copy.imgAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-2.5 py-1 rounded-md mf-caption font-semibold text-foreground flex items-center gap-1.5">
                    <Boxes className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{pick(lang, copy.overlayBadge)}</span>
                  </div>
                </div>

                <CardContent className="p-4 bg-card">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                      <span>{pick(lang, copy.cardFooterNote)}</span>
                    </div>
                    <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 inline-flex items-center gap-1 shrink-0">
                      <span>{pick(lang, copy.cardFooterLink)}</span>
                      <ArrowRight className="size-3" aria-hidden="true" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* 4-Item Spec Strip under the grid */}
        <div className="mf-spec-strip">
          {specifications.map((spec, i) => <div key={i} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">
                {pick(lang, spec.label)}
              </span>
              <span className="mf-spec-strip__value font-mono">
                {spec.value}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}